/**
 * Settings (WP1-SETTINGS, BUILD-PLAN M01 "location setup"). Replaces the
 * shell's Settings placeholder (same id and `/settings` path).
 *
 * Practice profile and defaults, office hours and the scheduling grid,
 * operatories, providers, appointment types, schedule templates, fee
 * schedules, the PulseSync connection per office, integrations and personal
 * notification preferences. Users, roles, workstations and office identity
 * stay in the identity module; its pages appear in this module's section
 * rail when they exist.
 */
import { defineManifest } from '@velora/contracts';
import { defineWebModule } from '../../app/module.js';

export default defineWebModule({
  manifest: defineManifest({
    id: 'settings',
    title: 'Settings',
    version: '0.1.0',
    owner: 'WP1-SETTINGS',
    entitlementKey: null,
    permissions: ['settings.manage', 'offices.manage'],
    routes: [
      { id: 'home', path: '/settings', title: 'Settings', officeScope: 'practice' },
      {
        id: 'office-setup',
        path: '/settings/office-setup',
        title: 'Offices and hours',
        officeScope: 'location-or-all',
      },
      {
        id: 'providers',
        path: '/settings/providers',
        title: 'Providers',
        officeScope: 'location-or-all',
      },
      {
        id: 'appointment-types',
        path: '/settings/appointment-types',
        title: 'Appointment types',
        officeScope: 'location-or-all',
      },
      {
        id: 'schedule-templates',
        path: '/settings/schedule-templates',
        title: 'Schedule templates',
        officeScope: 'location',
      },
      // NEW-WF-03 schedule setup: provider hours and time off, holidays, schedule rules.
      {
        id: 'provider-hours',
        path: '/settings/provider-hours',
        title: 'Provider hours and time off',
        officeScope: 'location',
      },
      {
        id: 'holidays',
        path: '/settings/holidays',
        title: 'Holidays and closed days',
        officeScope: 'location',
      },
      {
        id: 'schedule-rules',
        path: '/settings/schedule-rules',
        title: 'Schedule rules',
        officeScope: 'location',
      },
      {
        id: 'opening-fill',
        path: '/settings/opening-fill',
        title: 'Opening fill',
        officeScope: 'location',
      },
      {
        id: 'appointment-statuses',
        path: '/settings/appointment-statuses',
        title: 'Appointment statuses',
        officeScope: 'location',
      },
      // Patient grade policies (off by default), under Scheduling in the section rail.
      {
        id: 'patient-grade',
        path: '/settings/patient-grade',
        title: 'Patient grade and policies',
        officeScope: 'location',
      },
      {
        id: 'fee-schedules',
        path: '/settings/fee-schedules',
        title: 'Fee schedules',
        officeScope: 'practice',
      },
      {
        id: 'pulsesync',
        path: '/settings/pulsesync',
        title: 'PulseSync',
        officeScope: 'location-or-all',
      },
      {
        // Get the Velora app (everyone): download, "Open in the app", what it adds.
        id: 'get-app',
        path: '/download',
        title: 'Download apps',
        officeScope: 'practice',
      },
      {
        // The desktop installer (workstation or office server) and enrollment codes.
        id: 'downloads',
        path: '/settings/downloads',
        title: 'Downloads and install',
        officeScope: 'practice',
        permission: 'settings.manage',
      },
      {
        id: 'integrations',
        path: '/settings/integrations',
        title: 'Integrations',
        officeScope: 'location-or-all',
      },
      {
        id: 'messaging',
        path: '/settings/messaging',
        title: 'Texting and email',
        officeScope: 'location-or-all',
      },
      {
        // Every automated patient text, per office (Zac 2026-11-01).
        id: 'communications',
        path: '/settings/communications',
        title: 'Communications',
        officeScope: 'location-or-all',
      },
      {
        id: 'payments',
        path: '/settings/payments',
        title: 'Payments',
        officeScope: 'location-or-all',
        permission: 'settings.manage',
      },
      {
        id: 'document-types',
        path: '/settings/document-types',
        title: 'Document types',
        officeScope: 'location',
      },
      {
        id: 'automations',
        path: '/settings/automations',
        title: 'Automations & notifications',
        officeScope: 'practice',
      },
      {
        id: 'notifications',
        path: '/settings/notifications',
        title: 'Notifications',
        officeScope: 'practice',
      },
      // Personal, so no permission: every employee picks their own mode.
      {
        id: 'appearance',
        path: '/settings/appearance',
        title: 'Appearance',
        officeScope: 'practice',
      },
    ],
    // Staff without settings.manage can still open setup read-only by link;
    // the nav offers it to the people who run setup.
    nav: [
      {
        label: 'Settings',
        group: 'settings',
        routeId: 'home',
        icon: 'settings',
        order: 10,
        permission: 'settings.manage',
      },
      {
        // W12: everyone can find the apps (desktop, office installers, Android).
        label: 'Download apps',
        group: 'settings',
        routeId: 'get-app',
        icon: 'download',
        order: 985,
      },
    ],
    commands: [
      {
        id: 'settings.open',
        title: 'Go to Settings',
        keywords: ['preferences', 'practice', 'profile'],
        routeId: 'home',
      },
      {
        id: 'settings.offices',
        title: 'Office hours and operatories',
        keywords: ['hours', 'operatory', 'chairs', 'grid', 'increment', 'add office', 'new office'],
        routeId: 'office-setup',
      },
      {
        id: 'settings.providers',
        title: 'Providers',
        keywords: ['dentist', 'hygienist', 'npi', 'doctor'],
        routeId: 'providers',
      },
      {
        id: 'settings.appointment-types',
        title: 'Appointment types',
        keywords: ['time pattern', 'length', 'visit types', 'online booking'],
        routeId: 'appointment-types',
      },
      {
        id: 'settings.templates',
        title: 'Schedule templates',
        keywords: ['perfect day', 'blocks', 'template'],
        routeId: 'schedule-templates',
      },
      {
        id: 'settings.provider-hours',
        title: 'Provider hours and time off',
        keywords: ['pto', 'time off', 'vacation', 'provider schedule', 'working hours', 'goals'],
        routeId: 'provider-hours',
      },
      {
        id: 'settings.holidays',
        title: 'Holidays and closed days',
        keywords: ['holiday', 'closed', 'christmas', 'thanksgiving'],
        routeId: 'holidays',
      },
      {
        id: 'settings.schedule-rules',
        title: 'Schedule rules and double booking',
        keywords: ['double booking', 'increment', 'triple', 'conflict', 'policy', 'hygiene chair'],
        routeId: 'schedule-rules',
      },
      {
        id: 'settings.appointment-statuses',
        title: 'Appointment statuses and colours',
        keywords: [
          'status',
          'firm',
          'here',
          'seated',
          'confirmed',
          'colour',
          'color',
          'practice definitions',
        ],
        routeId: 'appointment-statuses',
      },
      {
        id: 'settings.patient-grade',
        title: 'Patient grade and policies',
        keywords: [
          'grade',
          'grades',
          'patient grade',
          'grading',
          'thresholds',
          'reliability',
          'no-show',
          'prime time',
          'deposit',
          'card on file',
          'policy',
          'respect',
        ],
        routeId: 'patient-grade',
      },
      {
        id: 'settings.opening-fill',
        title: 'Opening fill: offer cancelled times automatically',
        keywords: [
          'asap',
          'cancellation',
          'broken',
          'waitlist',
          'fill',
          'cascade',
          'text',
          'ai call',
        ],
        routeId: 'opening-fill',
      },
      {
        id: 'settings.get-app',
        title: 'Get the Velora app for Windows',
        keywords: ['desktop app', 'download', 'install', 'windows', 'capture helper', 'call pop'],
        routeId: 'get-app',
      },
      {
        id: 'settings.downloads',
        title: 'Downloads and install Velora',
        keywords: [
          'install',
          'desktop app',
          'download',
          'office server',
          'workstation',
          'enrollment code',
          'rmm',
          'windows',
        ],
        routeId: 'downloads',
      },
      {
        id: 'settings.pulsesync',
        title: 'PulseSync connections',
        keywords: ['sync', 'pms', 'open dental', 'dentrix', 'eaglesoft', 'write-back', 'connector'],
        routeId: 'pulsesync',
      },
      {
        id: 'settings.integrations',
        title: 'Integrations',
        keywords: ['phones', 'texting', 'sms', 'imaging', 'dexis', 'ai', 'hipaa'],
        routeId: 'integrations',
      },
      {
        id: 'settings.payments',
        title: 'Payments and card processing',
        keywords: [
          'payments',
          'card processing',
          'square',
          'stripe',
          'sprout',
          'finix',
          'sandbox',
          'text to pay',
          'processor',
          'keys',
        ],
        routeId: 'payments',
        permission: 'settings.manage',
      },
      {
        id: 'settings.messaging',
        title: 'Texting and email channels',
        keywords: ['twilio', 'sms', 'a2p', '10dlc', 'email', 'smtp', 'unsubscribe', 'texting'],
        routeId: 'messaging',
      },
      {
        id: 'settings.communications',
        title: 'Communications: reminders, reviews, missed-call and auto-reply texts',
        keywords: [
          'communications',
          'reminder template',
          'appointment reminders',
          'confirm by text',
          'recall reminders',
          'review requests',
          'google review link',
          'missed call text',
          'after hours text',
          'auto reply',
          'cancel by text',
          'cancellation',
          'reschedule',
          'stop',
          'help',
          'text to pay',
          'sprout',
          'warp 9',
          'templates',
          'quiet hours',
          'segments',
        ],
        routeId: 'communications',
      },
      {
        id: 'settings.commsReminders',
        title: 'Appointment reminder templates and schedule',
        keywords: ['reminder template', '7 days', '1 day', 'same day', 'confirm', 'warp 9'],
        routeId: 'communications',
        search: { section: 'reminders' },
      },
      {
        id: 'settings.commsReviews',
        title: 'Review request settings and Google review link',
        keywords: ['review template', 'google', 'stars', 'threshold', 'private feedback'],
        routeId: 'communications',
        search: { section: 'reviews' },
      },
      {
        id: 'settings.commsMissedCall',
        title: 'Missed-call text templates (open and closed hours)',
        keywords: ['missed call', 'after hours', 'closed', 'text back'],
        routeId: 'communications',
        search: { section: 'missed_call' },
      },
      {
        id: 'settings.commsAutoReplies',
        title: 'Automatic replies: cancel and reschedule by text',
        keywords: ['cancel', 'cancellation', 'reschedule', 'auto reply', 'please call'],
        routeId: 'communications',
        search: { section: 'auto_replies' },
      },
      {
        id: 'settings.commsTextToPay',
        title: 'Text-to-pay (Sprout)',
        keywords: ['text to pay', 'payment link', 'sprout', 'balance'],
        routeId: 'communications',
        search: { section: 'text_to_pay' },
      },
      {
        id: 'settings.documentTypes',
        title: 'Document types',
        keywords: ['document type', 'document center', 'scan', 'folders', 'setup'],
        routeId: 'document-types',
        permission: 'settings.manage',
      },
      {
        id: 'settings.automations',
        title: 'Automations & notifications master switch',
        keywords: [
          'automations',
          'master switch',
          'turn off',
          'reminders',
          'dry run',
          'held',
          'testing',
        ],
        routeId: 'automations',
      },
      {
        id: 'settings.notifications',
        title: 'My notification preferences',
        keywords: ['alerts', 'email', 'quiet hours'],
        routeId: 'notifications',
      },
      {
        id: 'settings.appearance',
        title: 'Appearance and interface mode',
        keywords: [
          'theme',
          'mode',
          'skin',
          'dentrix',
          'open dental',
          'eaglesoft',
          'familiar',
          'classic',
          'dark',
          'light',
        ],
        routeId: 'appearance',
      },
    ],
    shortcuts: [{ keys: 'g ,', description: 'Go to Settings', routeId: 'home' }],
  }),
  pages: {
    home: () => import('./pages/PracticePage.js'),
    'office-setup': () => import('./pages/OfficeSetupPage.js'),
    providers: () => import('./pages/ProvidersPage.js'),
    'appointment-types': () => import('./pages/AppointmentTypesPage.js'),
    'schedule-templates': () => import('./pages/TemplatesPage.js'),
    'provider-hours': () => import('./pages/ProviderSchedulesPage.js'),
    holidays: () => import('./pages/HolidaysPage.js'),
    'schedule-rules': () => import('./pages/SchedulePolicyPage.js'),
    'appointment-statuses': () => import('./pages/ApptStatusesPage.js'),
    'patient-grade': () => import('./pages/PatientGradePage.js'),
    'opening-fill': () => import('./pages/OpeningFillPage.js'),
    // Moved to Insurance & fees (owner feedback 2026-10-21); the old path redirects.
    'fee-schedules': () =>
      import('../insurance-plans/LegacyRedirect.js').then((m) => ({ default: m.FeesRedirect })),
    appearance: () => import('./pages/AppearancePage.js'),
    pulsesync: () => import('./pages/PulseSyncPage.js'),
    downloads: () => import('./pages/DownloadsPage.js'),
    'get-app': () => import('./pages/GetAppPage.js'),
    integrations: () => import('./pages/IntegrationsPage.js'),
    notifications: () => import('./pages/NotificationsPage.js'),
    automations: () => import('./pages/AutomationsPage.js'),
    'document-types': () => import('./pages/DocumentTypesPage.js'),
    messaging: () => import('./pages/MessagingPage.js'),
    communications: () => import('./pages/CommunicationsPage.js'),
    payments: () => import('./pages/PaymentsSettingsPage.js'),
  },
});
